import { useEffect, useState } from "react";
import api, { formatMoney, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, StatCard, Panel, Pill, EmptyState } from "@/components/Bits";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  Baby, GraduationCap, CurrencyCircleDollar, ChartLineUp, Buildings,
  Bell, ShoppingBag, Calendar, ChalkboardTeacher, Sparkle, ChartBar,
  TrendUp, Path, UserPlus, IdentificationCard, ShieldStar, House,
  ArrowRight, Rocket,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PhoneInput from "@/components/PhoneInput";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, AreaChart, Area, LineChart, Line,
} from "recharts";

const COLORS = ["#E56B55", "#F4B731", "#A3C9A8", "#C2B8D9", "#6E6761", "#3A6B42"];

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [branchPerf, setBranchPerf] = useState([]);
  const [funnel, setFunnel] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [o, b, f, a, e] = await Promise.all([
          api.get("/analytics/overview"),
          api.get("/analytics/branch-performance"),
          api.get("/analytics/enquiry-funnel"),
          api.get("/analytics/attendance-trend?days=7"),
          api.get("/events"),
        ]);
        setOverview(o.data);
        setBranchPerf(b.data);
        setFunnel(f.data);
        setAttendance(a.data);
        setEvents(e.data.slice(0, 5));
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  return (
    <div>
      <PageHeader
        eyebrow="Super Admin"
        title="Your preschool network at a glance"
        subtitle="Live KPIs across every centre, in one warm view."
        action={
          <Link to="/app/ai" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white font-semibold hover:bg-black/90 transition" data-testid="admin-ai-link">
            <Sparkle weight="duotone" /> Open MangoCubs AI
          </Link>
        }
      />

      <QuickActions />

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Active students" value={overview.students_active} icon={Baby} accent="terracotta" hint="across all branches" testid="stat-active-students" />
          <StatCard label="Teachers" value={overview.teachers} icon={GraduationCap} accent="lavender" />
          <StatCard label="Conversion rate" value={`${overview.conversion_rate}%`} icon={TrendUp} accent="sage" hint={`${overview.admissions_converted}/${overview.enquiries_total} enquiries`} />
          <StatCard label="Fees collected" value={formatMoney(overview.fees_collected)} icon={CurrencyCircleDollar} accent="mustard" hint={`${formatMoney(overview.fees_due)} pending`} />
          <StatCard label="Attendance today" value={`${overview.attendance_today_pct}%`} icon={ChalkboardTeacher} accent="sage" />
          <StatCard label="Apparel orders" value={overview.apparel_orders_pending} icon={ShoppingBag} accent="terracotta" hint="pending fulfilment" />
          <StatCard label="Daily updates (7d)" value={overview.daily_updates_last7} icon={ChartLineUp} accent="lavender" />
          <StatCard label="Upcoming events" value={overview.upcoming_events} icon={Calendar} accent="mustard" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Panel title="Attendance trend (7 days)" className="lg:col-span-2" testid="panel-attendance-trend">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={attendance}>
                <defs>
                  <linearGradient id="attg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#A3C9A8" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#A3C9A8" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE5DF" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6E6761" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6E6761" }} />
                <Tooltip />
                <Area type="monotone" dataKey="pct" stroke="#3A6B42" fillOpacity={1} fill="url(#attg)" name="Attendance %" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Enquiry funnel" testid="panel-enquiry-funnel">
          {funnel.length === 0 ? <EmptyState /> : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={funnel} dataKey="count" nameKey="status" innerRadius={50} outerRadius={80} paddingAngle={3}>
                    {funnel.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Panel title="Branch performance" testid="panel-branch-performance" className="lg:col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-ink-soft text-xs uppercase tracking-wider">
                  <th className="py-2">Centre</th>
                  <th className="py-2">Students</th>
                  <th className="py-2">Enquiries</th>
                  <th className="py-2">Conv.</th>
                  <th className="py-2">Fees collected</th>
                  <th className="py-2">Due</th>
                </tr>
              </thead>
              <tbody>
                {branchPerf.map((b) => (
                  <tr key={b.branch_id} className="border-t border-border">
                    <td className="py-3">
                      <div className="font-semibold">{b.branch_name}</div>
                      <div className="text-xs text-ink-soft">{b.city}, {b.country}</div>
                    </td>
                    <td className="py-3 font-medium">{b.students}</td>
                    <td className="py-3">{b.enquiries}</td>
                    <td className="py-3"><Pill tone={b.conversion_rate > 30 ? "sage" : "mustard"}>{b.conversion_rate}%</Pill></td>
                    <td className="py-3 font-medium">{formatMoney(b.fees_collected, b.currency)}</td>
                    <td className="py-3 text-ink-soft">{formatMoney(b.fees_due, b.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Upcoming events" testid="panel-upcoming-events">
          {events.length === 0 ? <EmptyState title="No events" /> : (
            <div className="space-y-3">
              {events.map((e) => (
                <Link key={e.event_id} to="/app/events" className="block p-3 rounded-2xl hover:bg-paper-alt transition border border-transparent hover:border-border">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-mustard-soft text-mustard-hover flex items-center justify-center font-bold text-xs">{new Date(e.event_date).getDate()}</div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{e.name}</div>
                      <div className="text-xs text-ink-soft">{e.event_date} · {e.start_time || "—"}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}


/**
 * QuickActions — prominent CTAs on the super-admin landing screen so the
 * day-one workflow (spin up a branch, invite the HQ team, invite branch
 * admins) is one click away, not buried in the user management module.
 */
function QuickActions() {
  const [openBranch, setOpenBranch] = useState(false);
  const [openHq, setOpenHq] = useState(false);
  const [openBa, setOpenBa] = useState(false);

  const tiles = [
    { id: "branch", label: "Add a new branch", icon: Buildings, tone: "terracotta",
      hint: "Spin up a new MangoCubs centre + its admin in one go.",
      testid: "qa-branch", onClick: () => setOpenBranch(true) },
    { id: "hq", label: "Invite an HQ admin", icon: ShieldStar, tone: "lavender",
      hint: "Cross-branch admin with read access everywhere.",
      testid: "qa-hq", onClick: () => setOpenHq(true) },
    { id: "ba", label: "Invite a branch admin", icon: IdentificationCard, tone: "mustard",
      hint: "Day-to-day owner of a single branch.",
      testid: "qa-ba", onClick: () => setOpenBa(true) },
    { id: "users", label: "Manage all users", icon: UserPlus, tone: "sage",
      hint: "Full directory · bulk CSV upload · password resets.",
      testid: "qa-users", to: "/app/users" },
  ];

  return (
    <div className="mb-8" data-testid="quick-actions">
      <div className="flex items-center gap-2 mb-3">
        <Rocket weight="duotone" className="text-mango-deep" size={22} />
        <h2 className="font-heading font-bold text-lg">Set up your network</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map((t) => {
          const Icon = t.icon;
          const body = (
            <div className={`p-4 rounded-3xl border border-border bg-white hover:shadow-hoverlift transition cursor-pointer h-full`}>
              <div className={`w-11 h-11 rounded-2xl bg-${t.tone}-soft text-${t.tone}-deep inline-flex items-center justify-center mb-2`}>
                <Icon weight="duotone" size={22} />
              </div>
              <div className="font-semibold">{t.label}</div>
              <div className="text-xs text-ink-soft mt-1">{t.hint}</div>
              <div className="text-xs text-terracotta font-semibold mt-3 flex items-center gap-1">
                Open <ArrowRight size={12} weight="bold" />
              </div>
            </div>
          );
          return t.to
            ? <Link key={t.id} to={t.to} data-testid={t.testid}>{body}</Link>
            : <button key={t.id} onClick={t.onClick} data-testid={t.testid} className="text-left">{body}</button>;
        })}
      </div>

      {openBranch && <NewBranchWizard onClose={() => setOpenBranch(false)} />}
      {openHq && <QuickInviteDialog role="hq_admin" onClose={() => setOpenHq(false)} />}
      {openBa && <QuickInviteDialog role="branch_admin" onClose={() => setOpenBa(false)} />}
    </div>
  );
}

function NewBranchWizard({ onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [branch, setBranch] = useState({ name: "", city: "", code: "", phone: "", email: "" });
  const [createdBranch, setCreatedBranch] = useState(null);
  const [brand, setBrand] = useState({
    display_name: "", tagline: "",
    primary_color: "#F58A1F", accent_color: "#7B6451",
    logo_file: null, logo_preview: null,
  });
  const [admin, setAdmin] = useState({ name: "", email: "", phone: "" });
  const [adminPhoneValid, setAdminPhoneValid] = useState(false);
  const [busy, setBusy] = useState(false);

  const submitBranch = async (e) => {
    e?.preventDefault?.();
    if (!branch.name || !branch.city) { toast.error("Name + city are required."); return; }
    setBusy(true);
    try {
      // Drop empty optional strings so Pydantic EmailStr / numeric fields
      // don't reject the request.
      const payload = Object.fromEntries(
        Object.entries(branch).filter(([_, v]) => v != null && v !== "")
      );
      const { data } = await api.post("/branches", payload);
      setCreatedBranch(data);
      setBrand((b) => ({ ...b, display_name: data.name }));
      toast.success(`Branch '${data.name}' created`);
      setStep(2);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create branch");
    } finally { setBusy(false); }
  };

  const onPickLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Logo must be under 4 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setBrand((b) => ({ ...b, logo_file: f, logo_preview: reader.result }));
    reader.readAsDataURL(f);
  };

  const submitBranding = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      let logo_url = null;
      if (brand.logo_file) {
        const fd = new FormData();
        fd.append("file", brand.logo_file);
        const { data } = await api.post("/files/upload?folder=branding", fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        logo_url = data.url;
      }
      await api.patch(`/branding/branch/${createdBranch.branch_id}`, {
        display_name: brand.display_name || createdBranch.name,
        tagline: brand.tagline || null,
        primary_color: brand.primary_color,
        accent_color: brand.accent_color,
        ...(logo_url ? { logo_url } : {}),
      });
      toast.success("Branding saved");
      setStep(3);
    } catch (err) {
      // Branding is optional — don't block the wizard.
      toast.warning(formatApiErrorDetail(err.response?.data?.detail) || "Branding skipped — you can edit it later under Settings.");
      setStep(3);
    } finally { setBusy(false); }
  };

  const submitAdmin = async (e) => {
    e?.preventDefault?.();
    if (!admin.name || !admin.email) { toast.error("Name + email are required."); return; }
    if (admin.phone && !adminPhoneValid) { toast.error("Phone number is invalid."); return; }
    setBusy(true);
    try {
      await api.post("/users", {
        email: admin.email, name: admin.name,
        phone: admin.phone || null,
        role: "branch_admin",
        branch_id: createdBranch.branch_id,
        auto_password: true,
        send_reset_via: "email",
      });
      toast.success("Branch admin invited — they'll get an OTP to set their password.");
      onClose();
      navigate(`/app/users`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not invite admin");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="new-branch-wizard">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? "Add a new branch" : step === 2 ? "Make it look like home" : "Invite the first branch admin"}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Where is this centre? Branding + first admin come next."
              : step === 2
                ? `${createdBranch?.name} is live. Add a logo + brand colour so parent emails and gate-pass posters open with the right vibe.`
                : `Invite the person who'll run ${createdBranch?.name}.`}
          </DialogDescription>
          <div className="flex gap-1 mt-2" data-testid="wizard-progress">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`h-1 flex-1 rounded-full ${n <= step ? "bg-terracotta" : "bg-border"}`} />
            ))}
          </div>
        </DialogHeader>

        {step === 1 && (
          <form onSubmit={submitBranch} className="space-y-3">
            <input required placeholder="Branch name (e.g. MangoCubs Kids - Hyderabad)"
              value={branch.name} onChange={(e) => setBranch({ ...branch, name: e.target.value })}
              className="w-full h-11 px-3 rounded-xl border border-border" data-testid="nb-name" />
            <div className="grid grid-cols-2 gap-2">
              <input required placeholder="City" value={branch.city}
                onChange={(e) => setBranch({ ...branch, city: e.target.value })}
                className="h-11 px-3 rounded-xl border border-border" data-testid="nb-city" />
              <input placeholder="Code (e.g. MCHYD)" value={branch.code}
                onChange={(e) => setBranch({ ...branch, code: e.target.value.toUpperCase() })}
                className="h-11 px-3 rounded-xl border border-border font-mono" data-testid="nb-code" />
            </div>
            <PhoneInput value={branch.phone}
              onChange={(v) => setBranch({ ...branch, phone: v })}
              placeholder="Branch phone (with country code)" testId="nb-phone" />
            <input type="email" placeholder="Branch email (optional)" value={branch.email}
              onChange={(e) => setBranch({ ...branch, email: e.target.value })}
              className="w-full h-11 px-3 rounded-xl border border-border" />
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-border">Cancel</button>
              <button disabled={busy || !branch.name || !branch.city} type="submit"
                data-testid="nb-submit"
                className="flex-1 h-11 rounded-full bg-terracotta text-white font-semibold disabled:opacity-60">
                {busy ? "Creating…" : "Create branch →"}
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={submitBranding} className="space-y-3" data-testid="nb-branding-form">
            <input placeholder="Display name (defaults to branch name)" value={brand.display_name}
              onChange={(e) => setBrand({ ...brand, display_name: e.target.value })}
              className="w-full h-11 px-3 rounded-xl border border-border" data-testid="nb-display-name" />
            <input placeholder="Tagline (optional) — e.g. 'Where curiosity grows'"
              value={brand.tagline}
              onChange={(e) => setBrand({ ...brand, tagline: e.target.value })}
              className="w-full h-11 px-3 rounded-xl border border-border" data-testid="nb-tagline" />

            <div>
              <label className="text-xs font-semibold text-ink-soft uppercase tracking-wider">Logo</label>
              <div className="mt-1 flex items-center gap-3">
                <div className="w-16 h-16 rounded-2xl bg-paper border border-border flex items-center justify-center overflow-hidden">
                  {brand.logo_preview
                    ? <img src={brand.logo_preview} alt="logo" className="w-full h-full object-contain" data-testid="nb-logo-preview" />
                    : <Buildings size={28} weight="duotone" className="text-ink-soft" />}
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    onChange={onPickLogo} className="hidden" data-testid="nb-logo-input" />
                  <span className="px-4 py-2 rounded-full bg-white border border-border text-sm font-medium hover:bg-paper">
                    {brand.logo_file ? "Replace logo" : "Upload logo"}
                  </span>
                </label>
              </div>
              <p className="text-xs text-ink-soft mt-1">PNG / JPG / SVG · up to 4 MB · square works best.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <ColorField label="Primary"
                value={brand.primary_color}
                onChange={(v) => setBrand({ ...brand, primary_color: v })}
                testid="nb-primary" />
              <ColorField label="Accent"
                value={brand.accent_color}
                onChange={(v) => setBrand({ ...brand, accent_color: v })}
                testid="nb-accent" />
            </div>

            <div className="rounded-2xl border border-border p-3 flex items-center gap-3" data-testid="nb-brand-preview">
              <div className="w-10 h-10 rounded-xl"
                style={{ background: brand.primary_color }} />
              <div>
                <div className="font-heading font-bold leading-tight" style={{ color: brand.accent_color }}>
                  {brand.display_name || createdBranch?.name}
                </div>
                {brand.tagline && <div className="text-xs text-ink-soft">{brand.tagline}</div>}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setStep(3)} className="flex-1 h-11 rounded-full border border-border"
                data-testid="nb-skip-branding">Skip for now</button>
              <button disabled={busy} type="submit" data-testid="nb-branding-submit"
                className="flex-1 h-11 rounded-full bg-terracotta text-white font-semibold disabled:opacity-60">
                {busy ? "Saving…" : "Save & continue →"}
              </button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={submitAdmin} className="space-y-3">
            <input required placeholder="Admin's full name" value={admin.name}
              onChange={(e) => setAdmin({ ...admin, name: e.target.value })}
              className="w-full h-11 px-3 rounded-xl border border-border" data-testid="ba-name" />
            <input required type="email" placeholder="Email" value={admin.email}
              onChange={(e) => setAdmin({ ...admin, email: e.target.value })}
              className="w-full h-11 px-3 rounded-xl border border-border" data-testid="ba-email" />
            <PhoneInput value={admin.phone}
              onChange={(v) => setAdmin({ ...admin, phone: v })}
              onValidityChange={setAdminPhoneValid} testId="ba-phone" />
            <div className="rounded-2xl bg-mustard-soft p-3 text-xs flex gap-2">
              <Rocket weight="duotone" className="shrink-0 mt-0.5" />
              <div>An OTP will be emailed to <code>{admin.email || "their address"}</code> so they can set their own password on first sign-in.</div>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setStep(2)} className="flex-1 h-11 rounded-full border border-border">Back</button>
              <button disabled={busy} type="submit" data-testid="ba-submit"
                className="flex-1 h-11 rounded-full bg-terracotta text-white font-semibold disabled:opacity-60">
                {busy ? "Inviting…" : "Send invitation"}
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-ink-soft uppercase tracking-wider">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded-xl border border-border cursor-pointer"
          data-testid={`${testid}-color`} />
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
          className="flex-1 h-10 px-3 rounded-xl border border-border font-mono text-xs"
          data-testid={`${testid}-hex`} />
      </div>
    </div>
  );
}

function QuickInviteDialog({ role, onClose }) {
  const isHq = role === "hq_admin";
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [f, setF] = useState({ name: "", email: "", phone: "", branch_id: "" });
  const [phoneValid, setPhoneValid] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isHq) {
      api.get("/branches").then(({ data }) => setBranches(data || [])).catch(() => {});
    }
  }, [isHq]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!f.name || !f.email) { toast.error("Name + email are required."); return; }
    if (!isHq && !f.branch_id) { toast.error("Pick a branch."); return; }
    if (f.phone && !phoneValid) { toast.error("Phone number is invalid."); return; }
    setBusy(true);
    try {
      await api.post("/users", {
        email: f.email, name: f.name, phone: f.phone || null,
        role, branch_id: isHq ? null : f.branch_id,
        auto_password: true, send_reset_via: "email",
      });
      toast.success(`${isHq ? "HQ" : "Branch"} admin invited — OTP sent to ${f.email}.`);
      onClose(); navigate("/app/users");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not invite admin");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid={`invite-${role}`}>
        <DialogHeader>
          <DialogTitle>Invite a {isHq ? "HQ" : "Branch"} admin</DialogTitle>
          <DialogDescription>
            {isHq
              ? "HQ admins see every branch but operate as a cross-branch role. Useful for the founder or central operations."
              : "A branch admin runs a single centre — its parents, staff, finance and operations."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Full name" value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
            className="w-full h-11 px-3 rounded-xl border border-border" data-testid="inv-name" />
          <input required type="email" placeholder="Email" value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })}
            className="w-full h-11 px-3 rounded-xl border border-border" data-testid="inv-email" />
          <PhoneInput value={f.phone} onChange={(v) => setF({ ...f, phone: v })}
            onValidityChange={setPhoneValid} testId="inv-phone" />
          {!isHq && (
            <select required value={f.branch_id} data-testid="inv-branch"
              onChange={(e) => setF({ ...f, branch_id: e.target.value })}
              className="w-full h-11 px-3 rounded-xl border border-border">
              <option value="">— Pick a branch —</option>
              {branches.map((b) => (
                <option key={b.branch_id} value={b.branch_id}>{b.name || b.city}</option>
              ))}
            </select>
          )}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-full border border-border">Cancel</button>
            <button disabled={busy} type="submit" data-testid="inv-submit"
              className="flex-1 h-11 rounded-full bg-terracotta text-white font-semibold disabled:opacity-60">
              {busy ? "Inviting…" : "Send invitation"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

